import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextField from '../../components/molecules/TextField';
import css from './Input.css';

export const HINT_VARIANTS = {
    WARNING: 'warning',
    INFO: 'info',
};
const HINT_VARIANT_VALUES = Object.values(HINT_VARIANTS);

// Matches the CSS transition duration on `.messageSlot` (Input.css). Keeps the message text in
// the DOM long enough for the close animation to play before the spans unmount.
const MESSAGE_TRANSITION_MS = 180;

const Input = ({
    value,
    placeholder,
    onChange,
    onBlur,
    disabled,
    inputClassName,
    containerClass,
    error,
    hint,
    hintVariant = HINT_VARIANTS.WARNING,
    required = false,
    type = 'text',
    inputConfig,
}) => {
    const [hasBeenBlurred, setHasBeenBlurred] = useState(false);

    // What the message slot currently displays. Updates immediately when there's a message and
    // clears one transition-cycle later so the slot has content to collapse from.
    const [shown, setShown] = useState({ error, hint, hintVariant });
    useEffect(() => {
        if (error || hint) {
            setShown({ error, hint, hintVariant });
            return undefined;
        }
        const timer = setTimeout(() => setShown({ error: null, hint: null, hintVariant }), MESSAGE_TRANSITION_MS);
        return () => clearTimeout(timer);
    }, [error, hint, hintVariant]);

    // Don't paint the red required-border until the user has interacted at least once.
    const errorInput = error || (required && !value && hasBeenBlurred);
    const handleBlur = e => {
        setHasBeenBlurred(true);
        if (onBlur) onBlur(e);
    };
    const showRequiredOverlay = !disabled && required && !value && !!placeholder;
    const isOpen = !!error || !!hint;

    return (
        <div className={classNames(css.container, containerClass)}>
            <TextField
                type={type}
                value={value}
                onChange={onChange}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder={disabled || required ? null : placeholder}
                inputClassName={classNames(css.cellInput, { [css.errorInput]: errorInput }, inputClassName)}
                containerClass={css.textField}
                inputConfig={inputConfig}
            />
            {showRequiredOverlay && (
                <span
                    className={classNames(css.placeholder, {
                        // Stays shrunk while the slot is animating closed (`shown` clears in sync).
                        [css.withError]: !!shown.error,
                        [css.withHint]: !shown.error && !!shown.hint,
                    })}
                >
                    {placeholder} <span className={css.requiredStar}>*</span>
                </span>
            )}
            <div className={classNames(css.messageSlot, { [css.messageSlotVisible]: isOpen })}>
                <div className={css.messageSlotInner}>
                    {shown.error && <span className={css.errorHint}>{shown.error}</span>}
                    {!shown.error && shown.hint && (
                        <span className={classNames(css.hint, css[`hint${shown.hintVariant}`])}>{shown.hint}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

Input.propTypes = {
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
    inputClassName: PropTypes.string,
    containerClass: PropTypes.string,
    error: PropTypes.string,
    hint: PropTypes.string,
    hintVariant: PropTypes.oneOf(HINT_VARIANT_VALUES),
    required: PropTypes.bool,
    type: PropTypes.oneOf(['text', 'password', 'number', 'textarea']),
    inputConfig: PropTypes.objectOf(PropTypes.any),
};

export default Input;
